import React, { useEffect, useMemo, useState } from 'react';
import { Camera, Upload, CheckCircle2, AlertTriangle, ArrowLeft, FileText, Sparkles, Loader2, Pencil, Trash2 } from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  uploadPhotoImport, updatePhotoImport, postPhotoImport,
  listPhotoImports, listAccounts, getPhotoImport, deletePhotoImport,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands } from '../lib/format';

const RECEIPT_PREFIXES = ['1-11', '1-12']; // Kas & Bank + Piutang (include semua akun Kas/Bank/Piutang yg ada)
const BIAYA_CODES = ['6-1105', '6-2001', '6-2002', '6-4000'];

const backend = () => process.env.REACT_APP_BACKEND_URL || '';
const attUrl = (u) => (u && u.startsWith('http') ? u : `${backend()}${u}`);
const FIELDS = [
  { k: 'sub_total',       label: 'Sub Total' },
  { k: 'total_pokok',     label: 'Total Pokok' },
  { k: 'laba_kotor',      label: 'Laba Kotor',       computed: true },
  { k: 'pot_faktur',      label: 'Pot Faktur' },
  { k: 'biaya_msk_total', label: 'Biaya Msk Total' },
  { k: 'biaya_lain',      label: 'Biaya Lain' },
  { k: 'laba_jual',       label: 'Laba Jual',        computed: true },
];

const formatMoneyStr = (v) => (v == null || v === '' ? '' : formatThousands(String(v)));
const short = (iso) => {
  if (!iso || iso.length < 10) return iso || '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default function PhotoImportPage() {
  const [step, setStep] = useState('upload'); // upload | review | done
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [posting, setPosting] = useState(false);
  const [current, setCurrent] = useState(null); // { id, file_url, extracted, validation, duplicate, template_recognized }
  const [corrected, setCorrected] = useState({});
  const [receiptAcc, setReceiptAcc] = useState('');
  const [potIsDisc, setPotIsDisc] = useState(true);
  const [biayaAcc, setBiayaAcc] = useState('');
  const [shippingBorneBy, setShippingBorneBy] = useState('customer'); // customer | company
  const [journalDesc, setJournalDesc] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [history, setHistory] = useState([]);
  const [postedRefs, setPostedRefs] = useState([]);

  const refreshHistory = async () => {
    try { setHistory(await listPhotoImports()); } catch { /* noop */ }
  };
  useEffect(() => {
    listAccounts().then(setAccounts).catch(() => {});
    refreshHistory();
  }, []);

  // Default receipt account = BCA Play Sport Indonesia (1-1101) if user hasn't picked one yet
  useEffect(() => {
    if (receiptAcc) return;
    const def = accounts.find((a) => a.code === '1-1101');
    if (def) setReceiptAcc(def.id);
  }, [accounts, receiptAcc]);

  // Paste from clipboard (Ctrl/Cmd+V) works only in "upload" step
  useEffect(() => {
    if (step !== 'upload') return;
    const handlePaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            const named = new File([file], file.name || `paste-${Date.now()}.${ext}`, { type: file.type });
            onFile(named);
            return;
          }
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps


  }, [step, uploading]);

  const receiptOptions = useMemo(
    () => accounts
      .filter((a) => !a.is_header && a.type === 'asset'
        && RECEIPT_PREFIXES.some((p) => (a.code || '').startsWith(p)))
      .sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    [accounts]);
  const biayaOptions = useMemo(
    () => accounts.filter((a) => BIAYA_CODES.includes(a.code) && !a.is_header),
    [accounts]);

  const num = (k) => Number(parseNumber(corrected[k]) || 0);
  const lkExpected = Math.round(num('sub_total') - num('total_pokok'));
  // LJ = LK - Pot - Lain + BiayaMsk (Biaya Msk ada tanda '+' di laporan asli)
  const ljExpected = Math.round(lkExpected - num('pot_faktur') - num('biaya_lain') + num('biaya_msk_total'));
  const lkOk = Math.abs(num('laba_kotor') - lkExpected) <= 2;
  const ljOk = Math.abs(num('laba_jual') - ljExpected) <= 2;
  const balanced = lkOk && ljOk;
  const netBarang = Math.round(num('sub_total') - (potIsDisc ? num('pot_faktur') : 0));
  const shipping = Math.round(num('biaya_msk_total'));
  const receiptTotal = Math.round(netBarang + (shippingBorneBy === 'customer' ? shipping : 0));

  const onFile = async (file) => {
    if (!file) return;
    const okExt = /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!okExt) return toast.error('Format tidak didukung. Gunakan JPG/PNG/WEBP.');
    if (file.size > 10 * 1024 * 1024) return toast.error('Ukuran melebihi 10 MB');
    setUploading(true);
    try {
      const r = await uploadPhotoImport(file);
      setCurrent(r);
      // seed corrected with extracted (numbers as-is)
      const ex = r.extracted || {};
      setCorrected({
        sub_total: ex.sub_total ?? '',
        total_pokok: ex.total_pokok ?? '',
        laba_kotor: ex.laba_kotor ?? '',
        pot_faktur: ex.pot_faktur ?? '',
        biaya_msk_total: ex.biaya_msk_total ?? '',
        biaya_lain: ex.biaya_lain ?? '',
        laba_jual: ex.laba_jual ?? '',
      });
      setReceiptAcc(''); setPotIsDisc(true); setBiayaAcc('');
      setStep('review');
      toast.success('OCR selesai');
      if (r.duplicate) toast.warning('Foto ini kemungkinan sudah pernah diproses');
      if (!r.template_recognized) toast.warning('Jenis laporan belum otomatis dikenali');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal upload/OCR');
    } finally {
      setUploading(false);
    }
  };

  const handleFieldChange = (k, v) => {
    setCorrected((c) => ({ ...c, [k]: v }));
  };

  const autoFillComputed = () => {
    setCorrected((c) => ({
      ...c,
      laba_kotor: lkExpected,
      laba_jual: ljExpected,
    }));
  };

  const submitPost = async () => {
    if (!receiptAcc) return toast.error('Pilih akun penerimaan dulu');
    if (!potIsDisc && num('pot_faktur') > 0 && !biayaAcc)
      return toast.error('Pot Faktur bukan diskon: pilih akun alternatif');
    if (!balanced) return toast.error('Angka belum balance, perbaiki dulu');
    setPosting(true);
    try {
      const num2 = (v) => (v === '' || v == null ? 0 : Number(parseNumber(String(v))) || 0);
      const payload = {
        corrected: {
          sub_total: num2(corrected.sub_total),
          total_pokok: num2(corrected.total_pokok),
          laba_kotor: num2(corrected.laba_kotor),
          pot_faktur: num2(corrected.pot_faktur),
          biaya_msk_total: num2(corrected.biaya_msk_total),
          biaya_lain: num2(corrected.biaya_lain),
          laba_jual: num2(corrected.laba_jual),
        },
        receipt_account_id: receiptAcc,
        pot_is_sales_discount: potIsDisc,
        biaya_account_id: biayaAcc || '',
        shipping_borne_by: shippingBorneBy,
        journal_description: (journalDesc || '').trim(),
      };
      await updatePhotoImport(current.id, payload);
      const r = await postPhotoImport(current.id);
      setPostedRefs(r.journal_refs || []);
      setStep('done');
      toast.success('Jurnal berhasil diposting');
      refreshHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal posting jurnal');
    } finally {
      setPosting(false);
    }
  };

  const reset = () => {
    setStep('upload'); setCurrent(null); setCorrected({});
    setReceiptAcc(''); setPotIsDisc(true); setBiayaAcc(''); setJournalDesc(''); setPostedRefs([]);
  };

  const editHistory = async (h) => {
    if (h.status === 'posted') {
      return toast.error('Import sudah diposting. Hapus dulu jika ingin mengubah.');
    }
    try {
      const full = await getPhotoImport(h.id);
      setCurrent({
        id: full.id,
        file_url: full.file_url,
        file_name: full.file_name,
        extracted: full.extracted || {},
        duplicate: null,
        template_recognized: !!full.template,
        template_used: full.template,
      });
      const cor = full.corrected || full.extracted || {};
      setCorrected({
        sub_total: cor.sub_total ?? '',
        total_pokok: cor.total_pokok ?? '',
        laba_kotor: cor.laba_kotor ?? '',
        pot_faktur: cor.pot_faktur ?? '',
        biaya_msk_total: cor.biaya_msk_total ?? '',
        biaya_lain: cor.biaya_lain ?? '',
        laba_jual: cor.laba_jual ?? '',
      });
      setReceiptAcc(full.receipt_account_id || '');
      setPotIsDisc(full.pot_is_sales_discount !== false);
      setBiayaAcc(full.biaya_account_id || '');
      setJournalDesc(full.journal_description || '');
      setStep('review');
    } catch {
      toast.error('Gagal memuat import');
    }
  };

  const removeHistory = async (h) => {
    const extra = h.status === 'posted'
      ? `\n\nJurnal terkait (${(h.journal_refs || []).join(', ')}) juga akan dihapus.`
      : '';
    if (!window.confirm(`Hapus import ${short(h.report_date)} ${h.warehouse || ''}?${extra}`)) return;
    try {
      const r = await deletePhotoImport(h.id);
      toast.success(`Import dihapus${r.deleted_journals ? ` (${r.deleted_journals} jurnal terkait dihapus)` : ''}`);
      refreshHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  return (
    <AccountingLayout>
      <div className="space-y-6" data-testid="photo-import-page">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
            Import Foto Laporan
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl">
            Upload foto laporan harian → OCR AI → validasi otomatis → preview jurnal → posting.
            Sistem tidak akan pernah membuat jurnal tanpa konfirmasi Anda.
          </p>
        </div>

        {step === 'upload' && (
          <div
            onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
            onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer?.files?.[0];
              if (f) onFile(f);
            }}
            data-testid="upload-dropzone"
            className={`rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
              dragging
                ? 'border-emerald-600 bg-emerald-100/70 ring-4 ring-emerald-200'
                : 'border-emerald-300 bg-emerald-50/40'
            }`}
          >
            <Camera className="w-10 h-10 text-emerald-700 mx-auto mb-3" />
            <div className="text-lg font-semibold text-stone-900">
              {dragging ? 'Lepaskan foto di sini…' : 'Upload Foto Laporan'}
            </div>
            <div className="text-sm text-stone-600 mt-1">
              JPG, JPEG, PNG, WEBP · Maks 10 MB · <span className="text-emerald-800 font-medium">Drag & drop</span> atau tempel dengan <kbd className="px-1.5 py-0.5 rounded border border-stone-300 bg-white text-[10px] font-mono">Ctrl</kbd>+<kbd className="px-1.5 py-0.5 rounded border border-stone-300 bg-white text-[10px] font-mono">V</kbd>
            </div>
            <div className="mt-5">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="photo-file-input"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  disabled={uploading}
                />
                <span className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-5 rounded-lg inline-flex items-center gap-2 text-sm font-medium">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Membaca foto…' : 'Pilih File'}
                </span>
              </label>
            </div>
            {uploading && (
              <div className="mt-4 text-xs text-emerald-700 flex items-center justify-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Menganalisa gambar dengan AI…
              </div>
            )}
          </div>
        )}

        {step === 'review' && current && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT: Foto + hasil OCR */}
            <div className="space-y-4">
              <div className="rounded-xl border border-stone-200 bg-white p-3">
                <div className="text-xs text-stone-500 mb-2">Foto Sumber</div>
                <img
                  src={attUrl(current.file_url)}
                  alt="report"
                  className="w-full rounded-lg border border-stone-200 max-h-80 object-contain bg-stone-50"
                />
              </div>
              {current.duplicate && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Laporan ini kemungkinan sudah pernah diproses (
                    <span className="font-mono">{current.duplicate.journal_refs?.join(', ')}</span>).
                    Klik konfirmasi hanya bila Anda yakin memang ingin posting ulang.
                  </div>
                </div>
              )}
              {!current.template_recognized && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Jenis laporan belum otomatis dikenali. Sistem tetap memakai template <b>Rekap Laba Kotor Jual Harian</b> — periksa angka dulu.
                </div>
              )}
              <div className="rounded-xl border border-stone-200 bg-white p-4 text-sm">
                <div className="text-xs text-stone-500 mb-2">Info Laporan</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-stone-500">Tanggal:</span> <b>{short(current.extracted?.report_date)}</b></div>
                  <div><span className="text-stone-500">Gudang:</span> <b>{current.extracted?.warehouse || '—'}</b></div>
                </div>
              </div>
            </div>

            {/* RIGHT: Editable fields + account selection */}
            <div className="space-y-4">
              <div className="rounded-xl border border-stone-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-stone-900">Hasil Pembacaan (dapat diedit)</div>
                  <button
                    onClick={autoFillComputed}
                    data-testid="btn-auto-fill"
                    className="text-xs text-emerald-800 hover:underline"
                    title="Isi Laba Kotor & Laba Jual sesuai perhitungan"
                  >
                    Isi otomatis
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {FIELDS.map((f) => {
                    const val = corrected[f.k];
                    const isMismatch = (f.k === 'laba_kotor' && !lkOk) || (f.k === 'laba_jual' && !ljOk);
                    return (
                      <div key={f.k} className="grid grid-cols-[130px,1fr] items-center gap-2">
                        <Label className="text-xs text-stone-600">{f.label}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={formatMoneyStr(val)}
                            onChange={(e) => handleFieldChange(f.k, formatThousands(e.target.value))}
                            inputMode="decimal"
                            data-testid={`fld-${f.k}`}
                            className={isMismatch ? 'border-rose-400 focus:ring-rose-300' : ''}
                          />
                          {isMismatch && (
                            <span className="text-[11px] text-rose-600 whitespace-nowrap">
                              ≠ {formatRp(f.k === 'laba_kotor' ? lkExpected : ljExpected)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${balanced ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-rose-50 text-rose-800 border border-rose-200'} flex items-center gap-2`}>
                  {balanced ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span>
                    {balanced
                      ? 'Data laporan balance dengan perhitungan.'
                      : 'Data laporan tidak balance. Periksa hasil OCR sebelum membuat jurnal.'}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
                <div>
                  <Label className="text-xs text-stone-600 mb-1.5 block">
                    Uang penjualan diterima melalui:
                  </Label>
                  <Select value={receiptAcc} onValueChange={setReceiptAcc}>
                    <SelectTrigger data-testid="receipt-select"><SelectValue placeholder="Pilih akun…" /></SelectTrigger>
                    <SelectContent>
                      {receiptOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          <span className="font-mono text-xs mr-2">{a.code}</span>{a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {num('pot_faktur') > 0 && (
                  <div>
                    <Label className="text-xs text-stone-600 mb-1.5 block">
                      Apakah Pot. Faktur merupakan potongan penjualan?
                    </Label>
                    <div className="flex gap-2">
                      <Button
                        variant={potIsDisc ? 'default' : 'outline'}
                        onClick={() => setPotIsDisc(true)}
                        className={potIsDisc ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
                        data-testid="pot-yes">
                        Ya, potongan penjualan (4-1500)
                      </Button>
                      <Button
                        variant={!potIsDisc ? 'default' : 'outline'}
                        onClick={() => setPotIsDisc(false)}
                        className={!potIsDisc ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
                        data-testid="pot-no">
                        Tidak, pilih akun lain
                      </Button>
                    </div>
                    {!potIsDisc && (
                      <div className="mt-2">
                        <Label className="text-xs text-stone-600 mb-1.5 block">Akun untuk Pot Faktur:</Label>
                        <Select value={biayaAcc} onValueChange={setBiayaAcc}>
                          <SelectTrigger data-testid="biaya-select"><SelectValue placeholder="Pilih akun biaya…" /></SelectTrigger>
                          <SelectContent>
                            {biayaOptions.map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                <span className="font-mono text-xs mr-2">{a.code}</span>{a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {shipping > 0 && (
                  <div>
                    <Label className="text-xs text-stone-600 mb-1.5 block">
                      Biaya pengiriman ({formatRp(shipping)}) ditanggung oleh:
                    </Label>
                    <div className="flex gap-2">
                      <Button
                        variant={shippingBorneBy === 'customer' ? 'default' : 'outline'}
                        onClick={() => setShippingBorneBy('customer')}
                        className={shippingBorneBy === 'customer' ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
                        data-testid="ship-customer">
                        Customer (titipan 1-1150)
                      </Button>
                      <Button
                        variant={shippingBorneBy === 'company' ? 'default' : 'outline'}
                        onClick={() => setShippingBorneBy('company')}
                        className={shippingBorneBy === 'company' ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
                        data-testid="ship-company">
                        Perusahaan
                      </Button>
                    </div>
                    <div className="text-[11px] text-stone-500 mt-1.5">
                      {shippingBorneBy === 'customer'
                        ? `Kas diterima = Rp ${formatRp(num('sub_total') + shipping)}. Rp ${formatRp(shipping)} masuk ke akun Titipan (bukan pendapatan).`
                        : `Kas diterima = Rp ${formatRp(num('sub_total'))} saja. Biaya pengiriman dicatat terpisah nanti.`}
                    </div>
                  </div>
                )}
              </div>

              {/* Preview journal */}
              {receiptAcc && balanced && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 p-4" data-testid="preview-journal">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-emerald-800" />
                    <div className="text-sm font-semibold text-stone-900">Preview Jurnal (belum diposting)</div>
                  </div>
                  <div className="text-xs text-stone-500 mb-2">
                    Tanggal: <b className="text-stone-800">{short(current.extracted?.report_date)}</b>
                    &nbsp;·&nbsp; Rekap Penjualan Harian — {current.extracted?.warehouse}
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="journal-desc" className="text-[11px] text-stone-600 mb-1 block">
                      Deskripsi Jurnal <span className="text-stone-400">(bisa diedit sebelum posting)</span>
                    </Label>
                    <Input
                      id="journal-desc"
                      data-testid="journal-desc-input"
                      value={journalDesc}
                      onChange={(e) => setJournalDesc(e.target.value)}
                      placeholder={`Rekap Penjualan & HPP Harian - ${current.extracted?.warehouse || ''}`.trim().replace(/-\s*$/, '')}
                      className="h-8 text-sm bg-white"
                    />
                  </div>
                  <table className="w-full text-sm border border-stone-200 rounded bg-white">
                    <thead>
                      <tr className="border-b border-stone-200 bg-stone-50/60">
                        <th className="text-left text-[10px] font-semibold text-stone-500 uppercase tracking-wider px-2 py-1.5">Akun</th>
                        <th className="text-right text-[10px] font-semibold text-stone-500 uppercase tracking-wider px-2 py-1.5">Debit</th>
                        <th className="text-right text-[10px] font-semibold text-stone-500 uppercase tracking-wider px-2 py-1.5">Kredit</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-stone-100">
                        <td className="px-2 py-1.5">{accounts.find((a) => a.id === receiptAcc)?.name} <span className="text-[10px] text-stone-400">({accounts.find((a) => a.id === receiptAcc)?.code})</span></td>
                        <td className="px-2 py-1.5 text-right font-mono">{formatRp(receiptTotal)}</td>
                        <td />
                      </tr>
                      {num('pot_faktur') > 0 && (
                        <tr className="border-b border-stone-100">
                          <td className="px-2 py-1.5">
                            {potIsDisc
                              ? 'Potongan Jual (4-1500)'
                              : (accounts.find((a) => a.id === biayaAcc)?.name || 'Akun biaya (pilih)')}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">{formatRp(num('pot_faktur'))}</td>
                          <td />
                        </tr>
                      )}
                      <tr className="border-b border-stone-100">
                        <td className="px-2 py-1.5">Pendapatan Jual (4-1100)</td>
                        <td />
                        <td className="px-2 py-1.5 text-right font-mono">{formatRp(num('sub_total'))}</td>
                      </tr>
                      {shipping > 0 && shippingBorneBy === 'customer' && (
                        <tr className="border-b border-stone-100">
                          <td className="px-2 py-1.5">Titipan Biaya Pengiriman Customer (1-1150)</td>
                          <td />
                          <td className="px-2 py-1.5 text-right font-mono">{formatRp(shipping)}</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-stone-50 border-t-2 border-stone-300">
                      <tr>
                        <td className="px-2 py-1.5 font-semibold">Total</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold">{formatRp(receiptTotal + num('pot_faktur'))}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold">{formatRp(num('sub_total') + (shippingBorneBy === 'customer' ? shipping : 0))}</td>
                      </tr>
                    </tfoot>
                  </table>

                  {num('total_pokok') > 0 && (
                    <>
                      <div className="text-xs text-stone-500 mt-4 mb-2">Baris HPP (digabung dalam 1 jurnal)</div>
                      <table className="w-full text-sm border border-stone-200 rounded bg-white">
                        <tbody>
                          <tr className="border-b border-stone-100">
                            <td className="px-2 py-1.5">HPP (5-1300)</td>
                            <td className="px-2 py-1.5 text-right font-mono">{formatRp(num('total_pokok'))}</td>
                            <td />
                          </tr>
                          <tr>
                            <td className="px-2 py-1.5">Persediaan Barang (1-2010)</td>
                            <td />
                            <td className="px-2 py-1.5 text-right font-mono">{formatRp(num('total_pokok'))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <Button variant="outline" onClick={reset} data-testid="btn-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Kembali & Perbaiki
                </Button>
                <Button
                  onClick={submitPost}
                  disabled={posting || !balanced || !receiptAcc}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="btn-post">
                  {posting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Konfirmasi & Posting Jurnal
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto mb-3" />
            <div className="text-lg font-semibold text-emerald-900 mb-2">Jurnal berhasil diposting</div>
            <div className="text-sm text-emerald-800 mb-4">
              Ref jurnal: {postedRefs.map((r) => <span key={r} className="font-mono mx-1 bg-white px-2 py-0.5 rounded border border-emerald-200">{r}</span>)}
            </div>
            <Button onClick={reset} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="btn-new-upload">
              Upload Foto Lain
            </Button>
          </div>
        )}

        {/* History */}
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid="photo-history">
          <div className="px-5 py-3 border-b border-stone-200 bg-stone-50/60 text-sm font-semibold text-stone-700">
            Riwayat Import Foto
          </div>
          {history.length === 0 ? (
            <div className="py-10 text-center text-sm text-stone-500">Belum ada import.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Tanggal</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Gudang</th>
                  <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Sub Total</th>
                  <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Laba Jual</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Journal Refs</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Status</th>
                  <th className="text-left text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">User</th>
                  <th className="text-right text-xs font-semibold text-stone-500 uppercase tracking-wide px-4 py-2">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`hist-${h.id}`} className="border-b border-stone-100 last:border-0">
                    <td className="px-4 py-2 font-mono text-[13px]">{short(h.report_date)}</td>
                    <td className="px-4 py-2 text-stone-700">{h.warehouse || '—'}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatRp(h.corrected?.sub_total ?? h.extracted?.sub_total)}</td>
                    <td className="px-4 py-2 text-right font-mono">{formatRp(h.corrected?.laba_jual ?? h.extracted?.laba_jual)}</td>
                    <td className="px-4 py-2 font-mono text-[11px]">{(h.journal_refs || []).join(', ') || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${h.status === 'posted' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-stone-100 text-stone-700 border border-stone-200'}`}>
                        {h.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-stone-500">{h.uploader || '—'}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => editHistory(h)}
                          disabled={h.status === 'posted'}
                          title={h.status === 'posted' ? 'Sudah diposting — hapus dulu' : 'Lanjutkan / ubah'}
                          data-testid={`edit-hist-${h.id}`}
                          className={`p-1.5 rounded ${h.status === 'posted' ? 'text-stone-300 cursor-not-allowed' : 'text-stone-500 hover:bg-stone-100'}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => removeHistory(h)}
                          title={h.status === 'posted' ? 'Hapus (akan hapus jurnal terkait)' : 'Hapus'}
                          data-testid={`delete-hist-${h.id}`}
                          className="p-1.5 rounded text-rose-500 hover:bg-rose-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AccountingLayout>
  );
}
